
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useOutlet } from "react-router-dom";

import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";

import type { DashboardLayoutProps } from "./types/dashboardLayout.types";
import { useDashboardLayoutHook } from "./hooks/useDashboardLayoutHook";

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const {
    mounted,
    isMobile,
    mobileOpen,
    activeMenuItem,
    contentMarginLeftMd,
    sidebarLeft,
    handleDrawerToggle,
    handleMenuItemChange,
    handleSidebarItemClick,
  } = useDashboardLayoutHook({ children });


  const outlet = useOutlet();
  const { pathname } = useLocation();

  if (!mounted) return null;

  return (
    <div className="flex min-h-screen w-full min-w-0 overflow-hidden bg-slate-50">
      <AnimatePresence>
        {isMobile && mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[1200] bg-black/50"
            onClick={handleDrawerToggle}
          />
        )}
      </AnimatePresence>

      <div
        className="fixed z-[1300] h-[calc(100vh-24px)] transition-transform duration-300 ease-in-out"
        style={{
          top: 12,
          left: isMobile ? 0 : sidebarLeft,
          transform:
            isMobile && !mobileOpen
              ? "translateX(calc(-100% - 1px))"
              : "translateX(0)",
        }}
      >
        <Sidebar
          activeMenuItem={activeMenuItem}
          setActiveMenuItem={handleMenuItemChange}
          onItemClick={handleSidebarItemClick}
        />
      </div>

      <div
        className="flex min-h-screen min-w-0 flex-1 flex-col"
        style={{
          marginLeft: isMobile ? 0 : contentMarginLeftMd,
          transition: "margin-left 0.2s ease",
        }}
      >
        <TopBar
          onMenuToggle={handleDrawerToggle}
          activeMenuItem={activeMenuItem}
        />

        <div className="min-w-0 flex-1 overflow-auto">
          <motion.div
            // Keyed on the URL, not the sidebar's highlighted item: that is
            // synced from the URL in an effect, so keying on it remounted the
            // page (re-running its queries) right after its first render.
            key={children ? "dashboard-child" : pathname}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{
              duration: 0.5,
              delay: 0.1,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            className="h-full w-full min-w-0 bg-[#F9FAFB] p-4 md:p-8"
          >
            {
              children ?? outlet
            }
          </motion.div>
        </div>
      </div>
    </div>
  );
}